import {
  canonicalizeMemberApplicationOperationRequest,
  createSecureMemberApplicationOperationId,
} from "../../Members/memberApplicationOperation";
import { isDefinitiveEventSalesRefusal } from "./eventSalesOperation";
import { getUserFacingErrorText } from "../../../../utils/userFacingError";

/**
 * Staff "Invoice Sponsorship" creates one sponsorship request and then
 * approves it to create the invoice. The backend makes create idempotent by
 * operation ID and can look a create up by that ID, so the dashboard keeps
 * the ID in memory only. (Ajax Oct 7 2026: a browser-stored batch journal
 * from an older tab blocked every new sponsorship with "An unfinished
 * sponsorship batch exists".)
 */
export type StaffSponsorshipCreateAttempt = Readonly<{
  /** The event the create was sent for; the lookup reads it there. */
  eventId: string;
  /** Canonical operation-free create input the ID was minted for. */
  fingerprint: string;
  operationId: string;
  /** The create was sent and its result is not known yet. */
  outcomeUnknown: boolean;
}>;

export type StaffSponsorshipRecord = {
  id: string;
  invoice?: { id: string } | null;
};

export type StaffSponsorshipCreateResult<Sponsorship> =
  /** The sponsorship and its invoice exist. */
  | { kind: "invoiced"; sponsorship: Sponsorship }
  /** The sponsorship is saved but has no invoice yet: offer to finish it. */
  | { kind: "finish"; error: unknown | null; sponsorship: Sponsorship }
  /** The backend refused before it saved anything; staff can edit and retry. */
  | { kind: "refused"; error: unknown }
  /** The outcome was unknown and the lookup proved nothing was saved. */
  | { kind: "notSaved"; error: unknown }
  /** The outcome is still unknown; the next attempt looks it up first. */
  | { kind: "unconfirmed"; error: unknown };

export type StaffSponsorshipCreateRun<Sponsorship> = {
  /** The attempt to keep for the next click (null starts a new one). */
  attempt: StaffSponsorshipCreateAttempt | null;
  result: StaffSponsorshipCreateResult<Sponsorship>;
};

export type StaffSponsorshipCreateLookup<Sponsorship> = (
  operation: Pick<StaffSponsorshipCreateAttempt, "eventId" | "operationId">,
) => Promise<Sponsorship | null>;

export type RunStaffSponsorshipCreateInput<
  CreateInput extends { eventId: string },
  Sponsorship extends StaffSponsorshipRecord,
> = {
  /** Approves a saved sponsorship; resolves with its invoice or throws. */
  approve: (sponsorship: Sponsorship) => Promise<Sponsorship>;
  attempt: StaffSponsorshipCreateAttempt | null;
  /** Sends the create; resolves null when the save is not confirmed. */
  create: (
    input: CreateInput & { operationId: string },
  ) => Promise<Sponsorship | null>;
  createOperationId?: () => string;
  input: CreateInput;
  /** Reads the sponsorship saved by a create operation; throws on failure. */
  lookup: StaffSponsorshipCreateLookup<Sponsorship>;
};

/** Staff text for a sponsorship error; see getUserFacingErrorText. */
export const getStaffSponsorshipErrorMessage = (
  error: unknown,
  fallback: string,
): string => getUserFacingErrorText(error, fallback);

const getStaffSponsorshipCreateFingerprint = (input: object) => {
  const operationFreeInput = { ...input } as Record<string, unknown>;
  delete operationFreeInput.operationId;
  return canonicalizeMemberApplicationOperationRequest(operationFreeInput);
};

const hasInvoice = (sponsorship: StaffSponsorshipRecord) =>
  Boolean(sponsorship.invoice?.id);

/** Approves a saved sponsorship. Approval failure keeps the finish offer. */
export const finishStaffSponsorship = async <
  Sponsorship extends StaffSponsorshipRecord,
>({
  approve,
  sponsorship,
}: {
  approve: (sponsorship: Sponsorship) => Promise<Sponsorship>;
  sponsorship: Sponsorship;
}): Promise<StaffSponsorshipCreateResult<Sponsorship>> => {
  if (hasInvoice(sponsorship)) {
    return { kind: "invoiced", sponsorship };
  }

  try {
    const approved = await approve(sponsorship);
    return hasInvoice(approved)
      ? { kind: "invoiced", sponsorship: approved }
      : {
          error: new Error(
            "The sponsorship invoice could not be confirmed after approval.",
          ),
          kind: "finish",
          sponsorship,
        };
  } catch (error) {
    return { error, kind: "finish", sponsorship };
  }
};

const resolveFoundSponsorship = <Sponsorship extends StaffSponsorshipRecord>(
  sponsorship: Sponsorship,
  error: unknown | null,
): StaffSponsorshipCreateResult<Sponsorship> =>
  hasInvoice(sponsorship)
    ? { kind: "invoiced", sponsorship }
    : { error, kind: "finish", sponsorship };

/**
 * One click of Invoice Sponsorship. Unknown outcomes are looked up by the
 * operation ID before anything else is sent, so a lost response can never
 * create a second sponsorship. The same details reuse the same operation ID
 * (a late duplicate send replays on the backend); a definitive refusal or a
 * lookup that finds nothing lets changed details start a new ID.
 */
export const runStaffSponsorshipCreate = async <
  CreateInput extends { eventId: string },
  Sponsorship extends StaffSponsorshipRecord,
>({
  approve,
  attempt,
  create,
  createOperationId = createSecureMemberApplicationOperationId,
  input,
  lookup,
}: RunStaffSponsorshipCreateInput<CreateInput, Sponsorship>): Promise<
  StaffSponsorshipCreateRun<Sponsorship>
> => {
  let currentAttempt = attempt;

  if (currentAttempt?.outcomeUnknown) {
    let found: Sponsorship | null;
    try {
      found = await lookup(currentAttempt);
    } catch (error) {
      return {
        attempt: currentAttempt,
        result: { error, kind: "unconfirmed" },
      };
    }

    if (found) {
      return {
        attempt: { ...currentAttempt, outcomeUnknown: false },
        result: resolveFoundSponsorship(found, null),
      };
    }
    currentAttempt = { ...currentAttempt, outcomeUnknown: false };
  }

  const fingerprint = getStaffSponsorshipCreateFingerprint(input);
  const sentAttempt: StaffSponsorshipCreateAttempt = {
    eventId: input.eventId,
    fingerprint,
    operationId:
      currentAttempt?.fingerprint === fingerprint
        ? currentAttempt.operationId
        : createOperationId(),
    outcomeUnknown: true,
  };
  let created: Sponsorship | null;

  try {
    created = await create({ ...input, operationId: sentAttempt.operationId });
  } catch (error) {
    if (isDefinitiveEventSalesRefusal(error)) {
      return { attempt: null, result: { error, kind: "refused" } };
    }
    return lookUpUnknownCreate({ attempt: sentAttempt, error, lookup });
  }

  if (!created) {
    return lookUpUnknownCreate({
      attempt: sentAttempt,
      error: new Error("The sponsorship request could not be confirmed."),
      lookup,
    });
  }

  const savedAttempt = { ...sentAttempt, outcomeUnknown: false };
  const result = await finishStaffSponsorship({ approve, sponsorship: created });
  return {
    attempt: result.kind === "invoiced" ? null : savedAttempt,
    result,
  };
};

const lookUpUnknownCreate = async <Sponsorship extends StaffSponsorshipRecord>({
  attempt,
  error,
  lookup,
}: {
  attempt: StaffSponsorshipCreateAttempt;
  error: unknown;
  lookup: StaffSponsorshipCreateLookup<Sponsorship>;
}): Promise<StaffSponsorshipCreateRun<Sponsorship>> => {
  let found: Sponsorship | null;
  try {
    found = await lookup(attempt);
  } catch {
    return { attempt, result: { error, kind: "unconfirmed" } };
  }

  const resolvedAttempt = { ...attempt, outcomeUnknown: false };
  return found
    ? { attempt: resolvedAttempt, result: resolveFoundSponsorship(found, error) }
    : { attempt: resolvedAttempt, result: { error, kind: "notSaved" } };
};

// Browser keys written by the retired multi-unit sponsorship batch journal
// (Oct 2026). Nothing reads them now; they are removed so old data cannot
// linger in a long-lived tab.
const legacySponsorshipBatchJournalKeyPrefix = "cmp-event-sponsorship-batch:v1:";
const legacySponsorshipCreateKeyPattern =
  /^cmp-event-sales-operation:v1:CREATE_SPONSORSHIP:.*:new-sponsorship-unit-\d+$/;

type LegacyJournalStorage = Pick<
  Storage,
  "key" | "length" | "removeItem"
>;

const getDefaultSessionStorage = (): LegacyJournalStorage | undefined => {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
};

/** Removes legacy sponsorship batch keys. Returns how many were removed. */
export const clearLegacyEventSponsorshipBatchJournals = (
  storage: LegacyJournalStorage | undefined = getDefaultSessionStorage(),
): number => {
  if (!storage) {
    return 0;
  }

  try {
    const legacyKeys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (
        key &&
        (key.startsWith(legacySponsorshipBatchJournalKeyPrefix) ||
          legacySponsorshipCreateKeyPattern.test(key))
      ) {
        legacyKeys.push(key);
      }
    }
    legacyKeys.forEach((key) => storage.removeItem(key));
    return legacyKeys.length;
  } catch {
    // Browser storage can be blocked; nothing reads these keys any more.
    return 0;
  }
};
